import type { StoredRecordingStudioDraft } from './recordingStudioStorage'

const MAGIC = 'MIOSEGNO_STUDIO_V1\n'
const MAX_MANIFEST_BYTES = 8 * 1024 * 1024

interface SerializedTakeMeta {
  id: number
  number: number
  mimeType: string
  recordedAt: string
  offsetMs?: number
  timelineStartMs?: number
  blobOffset: number
  blobLength: number
}

interface SerializedDraft {
  projectTitle: string
  accompanimentSource: 'NONE' | 'UPLOAD' | 'RECORD'
  uploadedAccompanimentName?: string
  parts: StoredRecordingStudioDraft['parts']
  takesByTrack: Record<string, SerializedTakeMeta[]>
  activeTakeByTrack: Record<string, number>
  compEnabledByTrack?: StoredRecordingStudioDraft['compEnabledByTrack']
  compSegmentsByTrack?: StoredRecordingStudioDraft['compSegmentsByTrack']
  trackMixByKey?: StoredRecordingStudioDraft['trackMixByKey']
  latencyMode?: StoredRecordingStudioDraft['latencyMode']
  manualLatencyMs?: number
  latencyEstimateMs?: number
  countInBeats?: 0 | 2 | 4 | 8
  countInBpm?: number
  partialRecordingEnabled?: boolean
  recordingStartMs?: number
  preRollSeconds?: 0 | 2 | 4 | 8
  updatedAt: string
}

interface ProjectFileManifest {
  format: 'mioSegno-recording-studio'
  version: 1
  exportedAt: string
  draft: SerializedDraft
}

function encodeUtf8(value: string): Uint8Array {
  return new TextEncoder().encode(value)
}

function decodeUtf8(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes)
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy.buffer
}

export function projectFileMagic(): string {
  return MAGIC
}

export async function encodeRecordingStudioProject(draft: StoredRecordingStudioDraft): Promise<Blob> {
  const blobParts: Blob[] = []
  let blobOffset = 0
  const takesByTrack: Record<string, SerializedTakeMeta[]> = {}

  for (const [trackKey, takes] of Object.entries(draft.takesByTrack ?? {})) {
    takesByTrack[trackKey] = takes.map((take) => {
      const meta: SerializedTakeMeta = {
        id: take.id,
        number: take.number,
        mimeType: take.mimeType,
        recordedAt: take.recordedAt,
        offsetMs: take.offsetMs,
        timelineStartMs: take.timelineStartMs,
        blobOffset,
        blobLength: take.blob.size,
      }
      blobParts.push(take.blob)
      blobOffset += take.blob.size
      return meta
    })
  }

  const manifest: ProjectFileManifest = {
    format: 'mioSegno-recording-studio',
    version: 1,
    exportedAt: new Date().toISOString(),
    draft: {
      projectTitle: draft.projectTitle,
      accompanimentSource: draft.accompanimentSource,
      uploadedAccompanimentName: draft.uploadedAccompanimentName,
      parts: draft.parts,
      takesByTrack,
      activeTakeByTrack: draft.activeTakeByTrack,
      compEnabledByTrack: draft.compEnabledByTrack,
      compSegmentsByTrack: draft.compSegmentsByTrack,
      trackMixByKey: draft.trackMixByKey,
      latencyMode: draft.latencyMode,
      manualLatencyMs: draft.manualLatencyMs,
      latencyEstimateMs: draft.latencyEstimateMs,
      countInBeats: draft.countInBeats,
      countInBpm: draft.countInBpm,
      partialRecordingEnabled: draft.partialRecordingEnabled,
      recordingStartMs: draft.recordingStartMs,
      preRollSeconds: draft.preRollSeconds,
      updatedAt: draft.updatedAt,
    },
  }

  const manifestBytes = encodeUtf8(JSON.stringify(manifest))
  if (manifestBytes.byteLength > MAX_MANIFEST_BYTES) {
    throw new Error('PROJECT_MANIFEST_TOO_LARGE')
  }

  const lengthBytes = new Uint8Array(4)
  new DataView(lengthBytes.buffer).setUint32(0, manifestBytes.byteLength, true)

  return new Blob(
    [
      toArrayBuffer(encodeUtf8(MAGIC)),
      toArrayBuffer(lengthBytes),
      toArrayBuffer(manifestBytes),
      ...blobParts,
    ],
    { type: 'application/vnd.miosegno.studio' },
  )
}

export async function decodeRecordingStudioProject(file: Blob): Promise<StoredRecordingStudioDraft> {
  const magicBytes = encodeUtf8(MAGIC)
  const headerSize = magicBytes.byteLength + 4
  if (file.size < headerSize) throw new Error('PROJECT_FILE_INVALID')

  const header = new Uint8Array(await file.slice(0, headerSize).arrayBuffer())
  for (let i = 0; i < magicBytes.length; i += 1) {
    if (header[i] !== magicBytes[i]) throw new Error('PROJECT_FILE_INVALID_MAGIC')
  }

  const manifestLength = new DataView(
    header.buffer,
    header.byteOffset + magicBytes.length,
    4,
  ).getUint32(0, true)

  if (manifestLength <= 0 || manifestLength > MAX_MANIFEST_BYTES) {
    throw new Error('PROJECT_FILE_INVALID_MANIFEST_LENGTH')
  }

  const manifestStart = headerSize
  const manifestEnd = manifestStart + manifestLength
  if (manifestEnd > file.size) throw new Error('PROJECT_FILE_TRUNCATED')

  const manifestText = decodeUtf8(
    new Uint8Array(await file.slice(manifestStart, manifestEnd).arrayBuffer()),
  )
  const parsed = JSON.parse(manifestText) as ProjectFileManifest
  if (parsed.format !== 'mioSegno-recording-studio' || parsed.version !== 1 || !parsed.draft) {
    throw new Error('PROJECT_FILE_UNSUPPORTED')
  }

  const payloadStart = manifestEnd
  const takesByTrack: StoredRecordingStudioDraft['takesByTrack'] = {}

  for (const [trackKey, takes] of Object.entries(parsed.draft.takesByTrack ?? {})) {
    takesByTrack[trackKey] = takes.map((take) => {
      const start = payloadStart + take.blobOffset
      const end = start + take.blobLength
      if (take.blobOffset < 0 || take.blobLength < 0 || end > file.size) {
        throw new Error('PROJECT_FILE_INVALID_BLOB_RANGE')
      }

      return {
        id: take.id,
        number: take.number,
        mimeType: take.mimeType,
        recordedAt: take.recordedAt,
        offsetMs: take.offsetMs,
        timelineStartMs: take.timelineStartMs ?? 0,
        blob: file.slice(start, end, take.mimeType || 'application/octet-stream'),
      }
    })
  }

  return {
    projectTitle: parsed.draft.projectTitle ?? '',
    accompanimentSource: parsed.draft.accompanimentSource ?? 'NONE',
    uploadedAccompanimentName: parsed.draft.uploadedAccompanimentName ?? '',
    parts: Array.isArray(parsed.draft.parts) ? parsed.draft.parts : [],
    takesByTrack,
    activeTakeByTrack: parsed.draft.activeTakeByTrack ?? {},
    compEnabledByTrack: parsed.draft.compEnabledByTrack ?? {},
    compSegmentsByTrack: parsed.draft.compSegmentsByTrack ?? {},
    trackMixByKey: parsed.draft.trackMixByKey ?? {},
    latencyMode: parsed.draft.latencyMode ?? 'AUTO',
    manualLatencyMs: parsed.draft.manualLatencyMs ?? 0,
    latencyEstimateMs: parsed.draft.latencyEstimateMs ?? 0,
    countInBeats: parsed.draft.countInBeats ?? 0,
    countInBpm: parsed.draft.countInBpm ?? 120,
    partialRecordingEnabled: parsed.draft.partialRecordingEnabled ?? false,
    recordingStartMs: parsed.draft.recordingStartMs ?? 0,
    preRollSeconds: parsed.draft.preRollSeconds ?? 4,
    updatedAt: parsed.draft.updatedAt || new Date().toISOString(),
  }
}
